import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';
import { useSite } from '@/contexts/SiteContext';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart,
  ReferenceLine
} from 'recharts';
import { 
  Clock, 
  TrendingUp, 
  TrendingDown, 
  AlertCircle, 
  CheckCircle,
  Zap,
  Brain,
  Search,
  MessageSquare,
  Layers,
  Activity
} from 'lucide-react';

// Color palette for charts
const COLORS = {
  primary: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  purple: '#8b5cf6',
  indigo: '#6366f1',
  pink: '#ec4899',
  teal: '#14b8a6'
};

const PHASE_COLORS = {
  // Backend phases
  'Context Fetch': '#94a3b8',
  'Payload Prep': '#64748b',
  'Kafka Publish': '#475569',
  'Response Processing': '#334155',
  
  // Network phases
  'Kafka Transit': '#fbbf24',
  'Network': '#f59e0b',
  
  // Agent phases
  'Intent Classification': COLORS.purple,
  'Intent Analysis': COLORS.purple,
  'Embedding Generation': COLORS.indigo,
  'Embeddings': COLORS.indigo,
  'Semantic Search': COLORS.primary,
  'Search': COLORS.primary,
  'LLM Request': COLORS.warning,
  'LLM': COLORS.warning,
  'Follow-up Generation': COLORS.teal,
  'Follow-ups': COLORS.teal,
  'Response': '#1e293b',
  
  // New detailed timing phases
  'Sector Topics Load': COLORS.pink,
  'Sector Embedding': '#f472b6',
  'Site Sector Query': '#db2777',
  'Adjacent Topics': '#be185d',
  'Database Connection': '#9333ea',
  'RAG Documents': '#7c3aed',
  'Context Preparation': '#6d28d9'
};

export default function ChatbotPerformance() {
  const { selectedSite } = useSite();
  const [timeRange, setTimeRange] = useState('24h');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [distributionMode, setDistributionMode] = useState<'count' | 'percentage'>('count');
  
  // Data states
  const [overview, setOverview] = useState<any>({ data: [], summary: {} });
  const [phaseBreakdown, setPhaseBreakdown] = useState<any>({ phases: [] });
  const [intentBreakdown, setIntentBreakdown] = useState<any[]>([]);
  const [errorAnalysis, setErrorAnalysis] = useState<any[]>([]);
  const [siteComparison, setSiteComparison] = useState<any[]>([]);
  const [recentRequests, setRecentRequests] = useState<any[]>([]);

  // Automatically determine granularity based on time range
  const getGranularity = () => {
    switch (timeRange) {
      case '1h':
        return 'minute';  // Show minutes for last hour
      case '24h':
        return 'hour';    // Show hours for last 24h
      case '7d':
        return 'day';     // Show days for last 7 days
      case '30d':
        return 'day';     // Show days for last 30 days
      case '90d':
        return 'day';     // Show days for last 90 days
      default:
        return 'hour';
    }
  };

  // Calculate date range
  const getDateRange = () => {
    const endDate = new Date();
    let startDate = new Date();

    switch (timeRange) {
      case '1h':
        startDate.setHours(endDate.getHours() - 1);
        break;
      case '24h':
        startDate.setDate(endDate.getDate() - 1);
        break;
      case '7d':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case '30d':
        startDate.setDate(endDate.getDate() - 30);
        break;
      case '90d':
        startDate.setDate(endDate.getDate() - 90);
        break;
    }
    
    return { startDate: startDate.toISOString(), endDate: endDate.toISOString() };
  };

  // Fetch performance data
  const fetchPerformanceData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const { startDate, endDate } = getDateRange();
      const granularity = getGranularity(); // Get granularity based on time range
      const siteParam = selectedSite?._id ? `&siteId=${selectedSite._id}` : '';
      
      // Fetch all data in parallel
      const [overviewRes, phaseRes, intentRes, errorRes, recentRes] = await Promise.all([
        authFetch(`/chatbot-performance/overview?startDate=${startDate}&endDate=${endDate}&granularity=${granularity}${siteParam}`),
        authFetch(`/chatbot-performance/phase-breakdown?startDate=${startDate}&endDate=${endDate}${siteParam}`),
        authFetch(`/chatbot-performance/by-intent?startDate=${startDate}&endDate=${endDate}${siteParam}`),
        authFetch(`/chatbot-performance/errors?startDate=${startDate}&endDate=${endDate}${siteParam}`),
        authFetch(`/chatbot-performance/recent-requests?limit=20${siteParam}`)
      ]);
      
      // Also fetch site comparison if super admin
      let siteComparisonData = [];
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user.role === 'super_admin' && !selectedSite) {
          const siteCompRes = await authFetch(`/chatbot-performance/site-comparison?startDate=${startDate}&endDate=${endDate}`);
          if (siteCompRes.ok) {
            const data = await siteCompRes.json();
            siteComparisonData = data.sites || [];
          }
        }
      }
      
      if (!overviewRes.ok || !phaseRes.ok || !intentRes.ok || !errorRes.ok) {
        throw new Error('Failed to fetch performance data');
      }
      
      const overviewData = await overviewRes.json();
      const phaseData = await phaseRes.json();
      const intentData = await intentRes.json();
      const errorData = await errorRes.json();
      
      setOverview(overviewData);
      setPhaseBreakdown(phaseData);
      
      // Debug intent data
      console.log('[ChatbotPerformance] Intent data:', intentData);
      setIntentBreakdown(intentData.data || intentData || []);
      
      setErrorAnalysis(errorData.errors || []);
      setSiteComparison(siteComparisonData);
      
      // Set recent requests data
      if (recentRes.ok) {
        const recentData = await recentRes.json();
        setRecentRequests(recentData.requests || []);
      }
      
    } catch (err: any) {
      setError(err.message || 'Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPerformanceData();
  }, [selectedSite, timeRange]);

  // Format milliseconds to readable time
  const formatDuration = (ms: number) => {
    if (typeof ms !== 'number' || isNaN(ms)) return '0ms';
    if (ms < 1000) return `${Math.round(ms)}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  // Format X-axis tick based on granularity
  const formatXAxisTick = (value: string) => {
    const date = new Date(value);
    const granularity = getGranularity();
    
    switch (granularity) {
      case 'minute':
        // For minutes, show HH:MM format
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      case 'hour':
        // For hours, show HH:00 format
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      case 'day':
        // For days, show MMM DD format
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      default:
        return date.toLocaleTimeString([], { hour: '2-digit' });
    }
  };

  // Format percentage
  const formatPercentage = (value: number) => {
    if (typeof value !== 'number' || isNaN(value)) return '0%';
    return `${value.toFixed(1)}%`;
  };

  // Calculate trend
  const calculateTrend = (data: any[], metric: string) => {
    if (data.length < 2) return { value: 0, direction: 'neutral' };
    
    const recent = data.slice(0, Math.ceil(data.length / 2));
    const previous = data.slice(Math.ceil(data.length / 2));
    
    const recentAvg = recent.reduce((sum, d) => sum + (d[metric] || 0), 0) / recent.length;
    const previousAvg = previous.reduce((sum, d) => sum + (d[metric] || 0), 0) / previous.length;
    
    const change = ((recentAvg - previousAvg) / previousAvg) * 100;
    
    return {
      value: Math.abs(change),
      direction: change > 5 ? 'up' : change < -5 ? 'down' : 'neutral'
    };
  };

  const responseTrend = calculateTrend(overview.data || [], 'avg_total_ms');

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Chatbot Performance</h1>
          <p className="text-muted-foreground mt-1">
            Monitor response times and performance metrics
          </p>
        </div>
        
        <div className="flex gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1h">Last Hour</SelectItem>
              <SelectItem value="24h">Last 24 Hours</SelectItem>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="90d">Last 90 Days</SelectItem>
            </SelectContent>
          </Select>
          
          <Button onClick={fetchPerformanceData} variant="outline">
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Requests</CardTitle>
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {overview.summary?.total_requests?.toLocaleString() || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  {overview.summary?.unique_sessions || 0} unique sessions
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatDuration(overview.summary?.avg_total_ms || 0)}
                </div>
                <div className="flex items-center text-xs">
                  {responseTrend.direction === 'up' ? (
                    <TrendingUp className="h-3 w-3 text-red-500 mr-1" />
                  ) : responseTrend.direction === 'down' ? (
                    <TrendingDown className="h-3 w-3 text-green-500 mr-1" />
                  ) : null}
                  <span className={
                    responseTrend.direction === 'up' ? 'text-red-500' : 
                    responseTrend.direction === 'down' ? 'text-green-500' : 
                    'text-muted-foreground'
                  }>
                    {responseTrend.value.toFixed(1)}% vs previous period
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatPercentage(overview.summary?.success_rate || 0)}
                </div>
                <p className="text-xs text-muted-foreground">
                  {overview.summary?.failed_requests || 0} failed requests
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">P95 Response Time</CardTitle>
                <Zap className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatDuration(overview.summary?.median_response_time || 0)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Median response time
                </p>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="timeline" className="space-y-4">
            <TabsList>
              <TabsTrigger value="timeline">Timeline</TabsTrigger>
              <TabsTrigger value="phases">Phase Breakdown</TabsTrigger>
              <TabsTrigger value="intents">By Intent</TabsTrigger>
              <TabsTrigger value="errors">Errors</TabsTrigger>
              <TabsTrigger value="requests">Recent Requests</TabsTrigger>
              {siteComparison.length > 0 && (
                <TabsTrigger value="sites">Site Comparison</TabsTrigger>
              )}
            </TabsList>

            {/* Timeline Tab */}
            <TabsContent value="timeline" className="space-y-4">
              {/* TTFT Graph - New */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-yellow-500" />
                    Time To First Token (TTFT)
                  </CardTitle>
                  <CardDescription>
                    Track how quickly the first token is generated in streaming responses
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={overview.data?.map(item => ({
                        ...item,
                        avg_ttft_ms: item.avg_ttft_ms || null,
                        p50_ttft_ms: item.p50_ttft_ms || null,
                        p95_ttft_ms: item.p95_ttft_ms || null,
                        streaming_rate: item.streaming_enabled_rate || 0
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="time_bucket" 
                          tickFormatter={formatXAxisTick}
                        />
                        <YAxis yAxisId="left" tickFormatter={(value) => formatDuration(value)} />
                        <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => `${value}%`} />
                        <Tooltip 
                          labelFormatter={(value) => new Date(value).toLocaleString()}
                          formatter={(value: any, name: string) => {
                            if (name === 'Streaming Rate') return `${value}%`;
                            return formatDuration(value);
                          }}
                        />
                        <Legend />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="avg_ttft_ms" 
                          stroke={COLORS.warning} 
                          name="Avg TTFT"
                          strokeWidth={2}
                          connectNulls
                        />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="p50_ttft_ms" 
                          stroke={COLORS.success} 
                          name="P50 TTFT"
                          strokeDasharray="5 5"
                          connectNulls
                        />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="p95_ttft_ms" 
                          stroke={COLORS.danger} 
                          name="P95 TTFT"
                          connectNulls
                        />
                        <Line 
                          yAxisId="right"
                          type="monotone" 
                          dataKey="streaming_rate" 
                          stroke={COLORS.purple} 
                          name="Streaming Rate"
                          strokeWidth={1}
                          strokeDasharray="3 3"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-yellow-50 p-3 rounded">
                      <p className="font-medium text-yellow-800">TTFT Status</p>
                      <p className="text-yellow-600">
                        {overview.summary?.avg_ttft_ms ? 
                          `Average: ${formatDuration(overview.summary.avg_ttft_ms)}` : 
                          'TTFT measurement not enabled'}
                      </p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded">
                      <p className="font-medium text-blue-800">Streaming Adoption</p>
                      <p className="text-blue-600">
                        {overview.summary?.streaming_enabled_count || 0} of {overview.summary?.total_requests || 0} requests
                        ({((overview.summary?.streaming_enabled_count || 0) / (overview.summary?.total_requests || 1) * 100).toFixed(1)}%)
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Response Time Over Time</CardTitle>
                  <CardDescription>
                    Average, P50, P95, and P99 response times
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={overview.data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="time_bucket" 
                          tickFormatter={formatXAxisTick}
                        />
                        <YAxis tickFormatter={(value) => formatDuration(value)} />
                        <Tooltip 
                          labelFormatter={(value) => new Date(value).toLocaleString()}
                          formatter={(value: any) => formatDuration(value)}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="avg_total_ms" 
                          stroke={COLORS.primary} 
                          name="Average"
                          strokeWidth={2}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="p50_total_ms" 
                          stroke={COLORS.success} 
                          name="P50"
                          strokeDasharray="5 5"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="p95_total_ms" 
                          stroke={COLORS.warning} 
                          name="P95"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="p99_total_ms" 
                          stroke={COLORS.danger} 
                          name="P99"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Request Volume</CardTitle>
                    <CardDescription>
                      Total, successful, failed, and cached requests
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={overview.data}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="time_bucket" 
                            tickFormatter={formatXAxisTick}
                          />
                          <YAxis />
                          <Tooltip 
                            labelFormatter={(value) => new Date(value).toLocaleString()}
                          />
                          <Legend />
                          <Area 
                            type="monotone" 
                            dataKey="total_requests" 
                            stackId="1"
                            stroke={COLORS.primary} 
                            fill={COLORS.primary}
                            name="Total"
                          />
                          <Area 
                            type="monotone" 
                            dataKey="cached_requests" 
                            stackId="2"
                            stroke={COLORS.purple} 
                            fill={COLORS.purple}
                            name="Cached"
                          />
                          <Area 
                            type="monotone" 
                            dataKey="failed_requests" 
                            stackId="2"
                            stroke={COLORS.danger} 
                            fill={COLORS.danger}
                            name="Failed"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Phase Timing Trends</CardTitle>
                    <CardDescription>
                      Average time for each processing phase
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={overview.data}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="time_bucket" 
                            tickFormatter={formatXAxisTick}
                          />
                          <YAxis tickFormatter={(value) => formatDuration(value)} />
                          <Tooltip 
                            labelFormatter={(value) => new Date(value).toLocaleString()}
                            formatter={(value: any) => formatDuration(value)}
                          />
                          <Legend />
                          <Line 
                            type="monotone" 
                            dataKey="avg_intent_ms" 
                            stroke={PHASE_COLORS['Intent Classification']} 
                            name="Intent"
                            strokeWidth={2}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="avg_embedding_ms" 
                            stroke={PHASE_COLORS['Embedding Generation']} 
                            name="Embedding"
                            strokeWidth={2}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="avg_search_ms" 
                            stroke={PHASE_COLORS['Semantic Search']} 
                            name="Search"
                            strokeWidth={2}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="avg_llm_ms" 
                            stroke={PHASE_COLORS['LLM Request']} 
                            name="LLM"
                            strokeWidth={2}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* New Stacked Phase Timeline */}
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle>Detailed Phase Timeline</CardTitle>
                  <CardDescription>
                    Stacked view of all processing phases over time
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-96">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart 
                        data={overview.data?.map(item => ({
                          time_bucket: item.time_bucket,
                          // Only include the main sequential phases that don't overlap
                          'Context Fetch': item.avg_context_fetch_ms || 0,
                          'Payload Prep': item.avg_payload_prep_ms || 0,
                          'Kafka Publish': item.avg_kafka_publish_ms || 0,
                          'Network/Kafka': item.avg_kafka_wait_ms || 0,
                          'Intent': item.avg_intent_ms || 0,
                          'Embeddings': item.avg_embedding_ms || 0,
                          'Search': (item.avg_search_ms || 0) + (item.avg_cross_product_ms || 0),
                          'LLM': item.avg_llm_ms || 0,
                          'Follow-ups': item.avg_followup_ms || 0,
                          'Response': item.avg_response_ms || 0
                        })) || []}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="time_bucket" 
                          tickFormatter={formatXAxisTick}
                        />
                        <YAxis tickFormatter={(value) => formatDuration(value)} />
                        <Tooltip 
                          labelFormatter={(value) => new Date(value).toLocaleString()}
                          formatter={(value: any) => formatDuration(value)}
                        />
                        <Legend />
                        
                        {/* Backend phases */}
                        <Bar dataKey="Context Fetch" stackId="a" fill={PHASE_COLORS['Context Fetch']} />
                        <Bar dataKey="Payload Prep" stackId="a" fill={PHASE_COLORS['Payload Prep']} />
                        <Bar dataKey="Kafka Publish" stackId="a" fill={PHASE_COLORS['Kafka Publish']} />
                        
                        {/* Network */}
                        <Bar dataKey="Network/Kafka" stackId="a" fill={PHASE_COLORS['Network']} />
                        
                        {/* Agent phases */}
                        <Bar dataKey="Intent" stackId="a" fill={PHASE_COLORS['Intent Analysis']} />
                        <Bar dataKey="Embeddings" stackId="a" fill={PHASE_COLORS['Embeddings']} />
                        <Bar dataKey="Search" stackId="a" fill={PHASE_COLORS['Search']} />
                        <Bar dataKey="LLM" stackId="a" fill={PHASE_COLORS['LLM']} />
                        <Bar dataKey="Follow-ups" stackId="a" fill={PHASE_COLORS['Follow-ups']} />
                        <Bar dataKey="Response" stackId="a" fill={PHASE_COLORS['Response']} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground">
                      Each bar shows the complete request lifecycle from receipt to response delivery.
                      Hover over segments to see exact timing for each phase.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Phase Breakdown Tab */}
            <TabsContent value="phases" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Phase Contribution</CardTitle>
                    <CardDescription>
                      Percentage of total response time per phase
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={phaseBreakdown.phases}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percentage }) => `${name}: ${percentage}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="avg_ms"
                          >
                            {phaseBreakdown.phases.map((entry: any, index: number) => (
                              <Cell key={`cell-${index}`} fill={PHASE_COLORS[entry.name] || COLORS.primary} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value: any) => formatDuration(value)} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Phase Performance Metrics</CardTitle>
                    <CardDescription>
                      Average, P50, and P95 times for each phase
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {phaseBreakdown.phases.map((phase: any) => (
                        <div key={phase.name} className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="font-medium">{phase.name}</span>
                            <Badge variant="outline">
                              {phase.request_count} requests
                            </Badge>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-sm">
                            <div>
                              <span className="text-muted-foreground">Avg: </span>
                              <span className="font-medium">{formatDuration(phase.avg_ms)}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">P50: </span>
                              <span className="font-medium">{formatDuration(phase.p50_ms)}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">P95: </span>
                              <span className="font-medium">{formatDuration(phase.p95_ms)}</span>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="h-2 rounded-full"
                              style={{
                                width: `${phase.percentage}%`,
                                backgroundColor: PHASE_COLORS[phase.name] || COLORS.primary
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Optional phases */}
              {(phaseBreakdown.optional?.cross_product_search?.request_count > 0 || 
                phaseBreakdown.optional?.rag_retrieval?.request_count > 0) && (
                <Card>
                  <CardHeader>
                    <CardTitle>Optional Processing Phases</CardTitle>
                    <CardDescription>
                      Additional processing that may occur based on query type
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {phaseBreakdown.optional?.cross_product_search?.request_count > 0 && (
                        <div className="flex justify-between items-center p-4 border rounded-lg">
                          <div>
                            <div className="font-medium">Cross-Product Search</div>
                            <div className="text-sm text-muted-foreground">
                              {phaseBreakdown.optional.cross_product_search.request_count} requests
                            </div>
                          </div>
                          <div className="text-2xl font-bold">
                            {formatDuration(phaseBreakdown.optional.cross_product_search.avg_ms)}
                          </div>
                        </div>
                      )}
                      
                      {phaseBreakdown.optional?.rag_retrieval?.request_count > 0 && (
                        <div className="flex justify-between items-center p-4 border rounded-lg">
                          <div>
                            <div className="font-medium">RAG Document Retrieval</div>
                            <div className="text-sm text-muted-foreground">
                              {phaseBreakdown.optional.rag_retrieval.request_count} requests
                            </div>
                          </div>
                          <div className="text-2xl font-bold">
                            {formatDuration(phaseBreakdown.optional.rag_retrieval.avg_ms)}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* By Intent Tab */}
            <TabsContent value="intents" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Performance by Intent Type Over Time</CardTitle>
                  <CardDescription>
                    How different query types perform over time
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {intentBreakdown.length === 0 ? (
                    <Alert className="mx-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        No intent type data available for this time period. Intent classification data will appear here once chatbot requests are processed.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <div className="h-96">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart 
                          data={overview.data}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="time_bucket" 
                            tickFormatter={formatXAxisTick}
                          />
                          <YAxis 
                            tickFormatter={(value) => formatDuration(value)}
                          />
                          <Tooltip 
                            labelFormatter={(value) => new Date(value).toLocaleString()}
                            formatter={(value: any) => formatDuration(value)} 
                          />
                          <Legend />
                          {/* Show a line for each intent type if data exists */}
                          <Line 
                            type="monotone" 
                            dataKey="avg_page_specific_ms" 
                            stroke={COLORS.primary} 
                            name="Page Specific"
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            connectNulls
                          />
                          <Line 
                            type="monotone" 
                            dataKey="avg_cross_product_ms" 
                            stroke={COLORS.success} 
                            name="Cross Product"
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            connectNulls
                          />
                          <Line 
                            type="monotone" 
                            dataKey="avg_buying_guidance_ms" 
                            stroke={COLORS.warning} 
                            name="Buying Guidance"
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            connectNulls
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Request Distribution by Intent</CardTitle>
                      <div className="flex gap-2">
                        <Button
                          variant={distributionMode === 'count' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setDistributionMode('count')}
                        >
                          Count
                        </Button>
                        <Button
                          variant={distributionMode === 'percentage' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setDistributionMode('percentage')}
                        >
                          %
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={intentBreakdown}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={(entry) => {
                              if (distributionMode === 'percentage') {
                                const total = intentBreakdown.reduce((sum, item) => sum + item.request_count, 0);
                                const percentage = ((entry.request_count / total) * 100).toFixed(1);
                                return `${entry.intent_type}: ${percentage}%`;
                              }
                              return `${entry.intent_type}: ${entry.request_count}`;
                            }}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="request_count"
                          >
                            {intentBreakdown.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={Object.values(COLORS)[index % Object.values(COLORS).length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Intent Performance Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {intentBreakdown.map((intent) => (
                        <div key={intent.intent_type} className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="font-medium">{intent.intent_type}</span>
                            <div className="flex gap-2">
                              <Badge variant={intent.success_rate >= 95 ? 'default' : 'destructive'}>
                                {formatPercentage(intent.success_rate)} success
                              </Badge>
                              {intent.error_count > 0 && (
                                <Badge variant="destructive">
                                  {intent.error_count} errors
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {intent.request_count} requests • {formatDuration(intent.avg_duration_ms)} avg
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Errors Tab */}
            <TabsContent value="errors" className="space-y-4">
              {errorAnalysis.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center h-64">
                    <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
                    <p className="text-lg font-medium">No errors in this time period</p>
                    <p className="text-sm text-muted-foreground">
                      All chatbot requests completed successfully
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle>Error Analysis</CardTitle>
                    <CardDescription>
                      Most common errors and their frequency
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {errorAnalysis.map((error, index) => (
                        <div key={index} className="border rounded-lg p-4 space-y-2">
                          <div className="flex justify-between items-start">
                            <div className="space-y-1 flex-1">
                              <div className="flex items-center gap-2">
                                <AlertCircle className="h-4 w-4 text-red-500" />
                                <span className="font-medium">{error.error_phase}</span>
                                <Badge variant="destructive">
                                  {error.error_count} occurrences
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground font-mono">
                                {error.error_message}
                              </p>
                            </div>
                          </div>
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>First: {new Date(error.first_occurrence).toLocaleString()}</span>
                            <span>Last: {new Date(error.last_occurrence).toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Recent Requests Tab */}
            <TabsContent value="requests" className="space-y-4">
              {/* Timing Breakdown Chart - Shows actual request flow */}
              <Card>
                <CardHeader>
                  <CardTitle>Request Timing Breakdown Over Time</CardTitle>
                  <CardDescription>
                    All individual processing phases shown as a stacked timeline
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={overview.data || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis 
                          dataKey="time_bucket" 
                          tickFormatter={formatXAxisTick}
                        />
                        <YAxis />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                          labelFormatter={(value) => new Date(value).toLocaleString()}
                          formatter={(value: any) => `${Math.round(value)}ms`}
                        />
                        <Legend />
                        
                        {/* Show all individual phases separately */}
                        <Bar dataKey="avg_context_fetch_ms" name="Context Fetch" stackId="a" fill={PHASE_COLORS['Context Fetch']} minPointSize={1} />
                        <Bar dataKey="avg_payload_prep_ms" name="Payload Prep" stackId="a" fill={PHASE_COLORS['Payload Prep']} minPointSize={1} />
                        <Bar dataKey="avg_sector_topics_ms" name="Sector Topics" stackId="a" fill={PHASE_COLORS['Sector Topics Load']} minPointSize={1} />
                        <Bar dataKey="avg_sector_embedding_ms" name="Sector Embed" stackId="a" fill={PHASE_COLORS['Sector Embedding']} minPointSize={1} />
                        <Bar dataKey="avg_intent_ms" name="Intent" stackId="a" fill={PHASE_COLORS['Intent Analysis']} minPointSize={1} />
                        <Bar dataKey="avg_embedding_ms" name="Embeddings" stackId="a" fill={PHASE_COLORS['Embeddings']} minPointSize={1} />
                        <Bar dataKey="avg_search_ms" name="Search" stackId="a" fill={PHASE_COLORS['Search']} minPointSize={1} />
                        <Bar dataKey="avg_llm_ms" name="LLM" stackId="a" fill={PHASE_COLORS['LLM']} minPointSize={1} />
                        <Bar dataKey="avg_site_sector_ms" name="Site Sector" stackId="a" fill={PHASE_COLORS['Site Sector Query']} minPointSize={1} />
                        <Bar dataKey="avg_adjacent_ms" name="Adjacent Topics" stackId="a" fill={PHASE_COLORS['Adjacent Topics']} minPointSize={1} />
                        <Bar dataKey="avg_db_connect_ms" name="DB Connect" stackId="a" fill={PHASE_COLORS['Database Connection']} minPointSize={1} />
                        <Bar dataKey="avg_rag_ms" name="RAG Docs" stackId="a" fill={PHASE_COLORS['RAG Documents']} minPointSize={1} />
                        <Bar dataKey="avg_context_prep_ms" name="Context Prep" stackId="a" fill={PHASE_COLORS['Context Preparation']} minPointSize={1} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recent Request Timelines</CardTitle>
                  <CardDescription>
                    Detailed phase breakdown for the most recent {recentRequests.length} requests
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {recentRequests.length === 0 ? (
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          No recent requests found. Make some chatbot requests to see timeline data here.
                        </AlertDescription>
                      </Alert>
                    ) : (
                      recentRequests.map((request: any) => (
                        <div key={request.trace_id} className="border rounded-lg p-4 space-y-3">
                          {/* Request header */}
                          <div className="flex justify-between items-start">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Badge variant={request.error_occurred ? "destructive" : "default"}>
                                  {request.error_occurred ? "Failed" : "Success"}
                                </Badge>
                                <span className="text-sm font-mono text-muted-foreground">
                                  {request.trace_id.substring(0, 8)}...
                                </span>
                                {request.intent_type && (
                                  <Badge variant="outline">{request.intent_type}</Badge>
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {formatDuration(request.total_duration_ms)} total • 
                                {request.query_length} chars query • 
                                {request.response_length} chars response • 
                                {new Date(request.created_at).toLocaleTimeString()}
                              </div>
                            </div>
                            <div className="text-right text-sm">
                              <div className="font-medium">{formatDuration(request.total_duration_ms)}</div>
                              <div className="text-muted-foreground">Total Time</div>
                            </div>
                          </div>

                          {/* Timeline visualization */}
                          <div className="space-y-2">
                            <div className="text-xs text-muted-foreground mb-1">Phase Timeline:</div>
                            <div className="relative h-8 bg-gray-100 rounded overflow-hidden">
                              {request.phases && request.phases.map((phase: any, index: number) => (
                                <div
                                  key={index}
                                  className="absolute h-full flex items-center px-2 text-xs font-medium text-white"
                                  style={{
                                    left: `${(phase.start / request.total_duration_ms) * 100}%`,
                                    width: `${(phase.duration / request.total_duration_ms) * 100}%`,
                                    backgroundColor: PHASE_COLORS[phase.name] || COLORS.primary,
                                    minWidth: '2px'
                                  }}
                                  title={`${phase.name}: ${formatDuration(phase.duration)} (${phase.start}ms - ${phase.start + phase.duration}ms)`}
                                >
                                  {phase.duration > request.total_duration_ms * 0.1 && phase.name}
                                </div>
                              ))}
                            </div>
                            
                            {/* Phase details */}
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
                              {request.phases && request.phases.map((phase: any, index: number) => (
                                <div key={index} className="flex items-center gap-1">
                                  <div 
                                    className="w-3 h-3 rounded-sm" 
                                    style={{ backgroundColor: PHASE_COLORS[phase.name] || COLORS.primary }}
                                  />
                                  <span className="font-medium">{phase.name}:</span>
                                  <span>{formatDuration(phase.duration)}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Error details */}
                          {request.error_occurred && (
                            <Alert variant="destructive">
                              <AlertCircle className="h-4 w-4" />
                              <AlertDescription>
                                Error in {request.error_phase}: {request.error_message}
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Site Comparison Tab (Super Admin Only) */}
            {siteComparison.length > 0 && (
              <TabsContent value="sites" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Site Performance Comparison</CardTitle>
                    <CardDescription>
                      Compare chatbot performance across all sites
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">Site</th>
                            <th className="text-right p-2">Requests</th>
                            <th className="text-right p-2">Avg Response</th>
                            <th className="text-right p-2">P50</th>
                            <th className="text-right p-2">P95</th>
                            <th className="text-right p-2">Success Rate</th>
                            <th className="text-right p-2">Sessions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {siteComparison.map((site) => (
                            <tr key={site.site_id} className="border-b hover:bg-muted/50">
                              <td className="p-2">
                                <div>
                                  <div className="font-medium">{site.site_name}</div>
                                  <div className="text-xs text-muted-foreground">{site.site_url}</div>
                                </div>
                              </td>
                              <td className="text-right p-2">{site.total_requests}</td>
                              <td className="text-right p-2">{formatDuration(site.avg_total_ms)}</td>
                              <td className="text-right p-2">{formatDuration(site.p50_response_time)}</td>
                              <td className="text-right p-2">{formatDuration(site.p95_response_time)}</td>
                              <td className="text-right p-2">
                                <Badge variant={site.success_rate >= 95 ? 'default' : 'destructive'}>
                                  {formatPercentage(site.success_rate)}
                                </Badge>
                              </td>
                              <td className="text-right p-2">{site.unique_sessions}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        </div>
      )}
    </div>
  );
}